import { buttonBaseClasses } from "@mui/material/ButtonBase";
import { inputLabelClasses } from "@mui/material/InputLabel";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { alpha, type Components, type Theme } from "@mui/material/styles";

import { COLOR_SCHEME_ATTRIBUTE } from "@/src/theme/colorSchemeBootstrap";
import {
  DARK_MODE_IMAGE_FILTER,
  darkModeLinkColor,
  isDarkMode,
  visitedLinkColor,
  onPrimarySurfaceColor,
  onSnackbarSurfaceColor,
  primaryAccentColor,
  selectedTabLabelColor
} from "@/src/theme/helpers";
import { schemeStyles } from "@/src/theme/schemeStyles";

const focusVisibleStyles = (theme: Theme) => ({
  outline: `2px solid ${theme.palette.primary.main}`,
  outlineOffset: "2px",
  boxShadow: `0 0 0 4px ${theme.palette.semantic.primary.focus}`
});

const alertSeverities = ["success", "info", "warning", "error"] as const;

/**
 * Surface styles for a standard alert of one severity.
 *
 * The dark mode alert backgrounds only differ from Background/Paper by a faint
 * tint, so an alert sitting on a card had no visible edge. Dark mode therefore
 * drops the fill and reads as an outlined card: Severity/Main border and icon
 * with the Alert/Content ink. Light mode keeps the tinted fill.
 */
const alertSeverityStyles = (
  theme: Theme,
  severity: (typeof alertSeverities)[number]
) => {
  const accentColor = theme.palette[severity].main;

  return {
    backgroundColor: isDarkMode(theme)
      ? "transparent"
      : theme.palette.semantic[severity].alertBg,
    border: isDarkMode(theme) ? `1px solid ${accentColor}` : undefined,
    color: theme.palette.semantic[severity].alertText,
    ".MuiAlert-icon": {
      color: accentColor
    }
  };
};

const componentOverrides: Components<Theme> = {
  MuiCssBaseline: {
    // A bare element selector, so any component that sets its own `filter`
    // through `sx` or `styled` keeps it: those land in a class, which wins on
    // specificity. Deliberate treatments such as the knocked-out white logo on
    // the dealer auction card therefore survive untouched.
    //
    // Written against the scheme attribute rather than through
    // `withColorSchemeStyles` below: these are global styles, which have no
    // class for `applyStyles`' `&` to anchor to.
    styleOverrides: {
      // `text-underline-offset` inherits, so every underline (sx
      // `textDecoration`, text buttons, rich text) sits at the same distance
      // from the text as the `MuiLink` override below.
      body: {
        textUnderlineOffset: "3px"
      },
      [`*:where([${COLOR_SCHEME_ATTRIBUTE}="dark"]) img`]: {
        filter: DARK_MODE_IMAGE_FILTER
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      // MUI tints every dark mode Paper with a white gradient scaled to its
      // elevation, so a dialog (elevation 24) or a dealer auction card
      // (elevation 4) rendered several shades lighter than Background/Paper
      // even when the token was set explicitly. The design system carries its
      // own Misc/Elevation tokens for raised surfaces, so the automatic tint
      // is dropped and Paper reads the Figma surface colour.
      root: ({ theme }) =>
        isDarkMode(theme) ? { backgroundImage: "none" } : {}
    }
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true
    },
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-focusVisible": focusVisibleStyles(theme),
        "&.MuiButton-text": {
          textTransform: "none",
          textDecoration: "underline",
          "&:hover": {
            textDecoration: "underline"
          },
          "&.Mui-focusVisible": focusVisibleStyles(theme)
        }
      })
    }
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: "2rem",
        padding: "0.625rem 1.25rem"
      }
    },
    variants: [
      {
        props: { size: "small" },
        style: {
          padding: "0.25rem 0.75rem",
          fontSize: "0.75rem"
        }
      },
      {
        // Dark mode primary CTAs keep the Primary/Main surface but swap the
        // black `primary.contrastText` for Text/Primary so the label stays
        // readable on the warm dark brand colour.
        props: { variant: "contained", color: "primary" },
        style: ({ theme }) =>
          isDarkMode(theme)
            ? {
                backgroundColor: theme.palette.primary.main,
                color: onPrimarySurfaceColor(theme),
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                  color: onPrimarySurfaceColor(theme)
                }
              }
            : {}
      },
      {
        // Dark mode Secondary/Main is near-white, so a contained secondary
        // button would outrank the primary CTA. Sit it on a neutral elevated
        // surface instead, outlined via an inset shadow so it keeps the same
        // height as the primary button next to it.
        props: { variant: "contained", color: "secondary" },
        style: ({ theme }) =>
          isDarkMode(theme)
            ? {
                backgroundColor: theme.palette.misc.elevation8,
                color: theme.palette.text.primary,
                boxShadow: `inset 0 0 0 1px ${theme.palette.misc.outlineBorder}`,
                "&:hover": {
                  backgroundColor: theme.palette.misc.elevation12,
                  color: theme.palette.text.primary,
                  boxShadow: `inset 0 0 0 1px ${theme.palette.misc.outlineBorder}`
                }
              }
            : {}
      },
      {
        // Dark mode Primary/Main only reaches ~2.6:1 on the dark surfaces, so
        // an outlined primary button's label and border nearly disappear. Read
        // the label from Text/Primary and outline with the lighter brand tint,
        // which clears the 3:1 minimum for a control boundary.
        props: { variant: "outlined", color: "primary" },
        style: ({ theme }) =>
          isDarkMode(theme)
            ? {
                color: theme.palette.text.primary,
                borderColor: theme.palette.primary.light,
                "&:hover": {
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.primary.light,
                  backgroundColor: theme.palette.semantic.primary.hover
                },
                "&.Mui-disabled": {
                  color: theme.palette.text.disabled,
                  borderColor: theme.palette.action.disabledBackground
                }
              }
            : {}
      },
      {
        // Text buttons take their label straight from Primary/Main, which only
        // reaches ~2.6:1 on the dark surfaces. Read the label from Text/Primary
        // like the outlined primary rule; the underline keeps the affordance.
        props: { variant: "text", color: "primary" },
        style: ({ theme }) =>
          isDarkMode(theme)
            ? {
                color: theme.palette.text.primary,
                "&:hover": {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.semantic.primary.hover
                },
                "&.Mui-disabled": {
                  color: theme.palette.text.disabled
                }
              }
            : {}
      },
      {
        // MUI's dark disabled label (`action.disabled`) lands around 2.5:1 on
        // the disabled surface; Text/Disabled reaches ~3.5:1 and still reads
        // as disabled. Matters most for wizard Next buttons, which stay
        // disabled until the whole step validates.
        props: { variant: "contained" },
        style: ({ theme }) =>
          isDarkMode(theme)
            ? {
                "&.Mui-disabled": {
                  color: theme.palette.text.disabled
                }
              }
            : {}
      },
      {
        props: { variant: "text" },
        style: {
          "&.MuiButton-text": {
            textDecoration: "none",
            textTransform: "uppercase",
            "&:hover": {
              textDecoration: "none"
            }
          }
        }
      }
    ]
  },
  MuiChip: {
    variants: [
      {
        // Selected wizard selector chips sit on the Primary/Main surface, so
        // they follow the contained button rule and swap the black
        // `primary.contrastText` for Text/Primary in dark mode.
        props: { variant: "filled", color: "primary" },
        style: ({ theme }) =>
          isDarkMode(theme)
            ? {
                color: onPrimarySurfaceColor(theme),
                "&:hover": {
                  color: onPrimarySurfaceColor(theme)
                }
              }
            : {}
      },
      {
        // Unselected selector chips are only ~1.7:1 against the dark paper, so
        // the tap target barely reads as a control. An outline gives it an edge
        // and lets the selected state read as a fill rather than a tone shift.
        props: { variant: "filled", color: "default" },
        style: ({ theme }) =>
          isDarkMode(theme)
            ? {
                border: `1px solid ${theme.palette.misc.outlineBorder}`
              }
            : {}
      }
    ]
  },
  MuiCheckbox: {
    styleOverrides: {
      // Checkboxes follow the Secondary/Main checkbox from Figma in dark mode:
      // the checked box is the near-white Secondary/Main (#FAFAFA) with the
      // knocked-out tick showing the surface behind it, and the interaction
      // circle uses the neutral Secondary overlays (8% hover, 30% focus)
      // instead of MUI's reddish Primary/Main tint. Light mode keeps the brand
      // red, where a near-white box would disappear against the page.
      root: ({ theme }) =>
        isDarkMode(theme)
          ? {
              "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                color: theme.palette.secondary.main
              },
              "&:hover": {
                backgroundColor: theme.palette.semantic.secondary.hover,
                "@media (hover: none)": {
                  backgroundColor: "transparent"
                }
              },
              "&.Mui-focusVisible": {
                backgroundColor: theme.palette.semantic.secondary.focus
              }
            }
          : {}
    }
  },
  MuiRadio: {
    styleOverrides: {
      // Radios follow the same rule as the dark mode checkbox: the selected dot
      // reads near-white Secondary/Main (#FAFAFA) instead of the burnt Primary
      // red, which barely separates from the dark surface, and the interaction
      // circle uses the neutral Secondary overlays.
      root: ({ theme }) =>
        isDarkMode(theme)
          ? {
              "&.Mui-checked": {
                color: theme.palette.secondary.main
              },
              "&:hover": {
                backgroundColor: theme.palette.semantic.secondary.hover,
                "@media (hover: none)": {
                  backgroundColor: "transparent"
                }
              },
              "&.Mui-focusVisible": {
                backgroundColor: theme.palette.semantic.secondary.focus
              }
            }
          : {}
    }
  },
  MuiDialog: {
    styleOverrides: {
      // Every modal surface reads Background/Paper. Dialogs used to inherit it
      // from Paper's default and then get lightened by the elevation tint, so
      // each one landed on a slightly different shade depending on its
      // elevation. Pinning it here makes the token the rule rather than
      // something each modal has to set for itself.
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper
      })
    }
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: theme.typography.h4.fontSize,
        fontWeight: theme.typography.h4.fontWeight,
        lineHeight: theme.typography.h4.lineHeight,
        letterSpacing: theme.typography.h4.letterSpacing,
        padding: theme.spacing(2)
      })
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      // Focus used to repaint the outline in the brand red, which reads as a
      // validation error on a field the user just clicked into. Focus keeps the
      // neutral Text/Primary ink that hover already uses - near-white in dark
      // mode, near-black in light - and MUI's own 1px -> 2px bump carries the
      // state change. Red is left to mean error. Anchored on the input rather
      // than on MuiTextField so bare `Select` and `OutlinedInput` follow suit.
      //
      // These target `.MuiOutlinedInput-notchedOutline` rather than `fieldset`,
      // and the `:not()` guards are load-bearing rather than defensive: MUI's
      // own state rules are all `(root)(state)(notchedOutline)`, so an element
      // selector loses to them outright and a plain class selector only ties.
      // Excluding the error state both lifts these above MUI's focus rule and
      // keeps an invalid field red while it has focus.
      root: ({ theme }) => ({
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.palette.misc.outlineBorder
        },
        [`&:hover:not(.${outlinedInputClasses.error}):not(.${outlinedInputClasses.disabled}) .${outlinedInputClasses.notchedOutline}`]:
          {
            borderColor: theme.palette.text.primary
          },
        [`&.${outlinedInputClasses.focused}:not(.${outlinedInputClasses.error}) .${outlinedInputClasses.notchedOutline}`]:
          {
            borderColor: theme.palette.text.primary
          }
      })
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        // MUI defaults to body1 (16px) and scales to ~12px on shrink. Applying
        // inputLabel (12px) globally made empty-field labels look too small.
        ...theme.typography.body1,
        // The label followed the old brand-red focus ring, so it stayed red
        // after the outline went neutral. It tracks the outline instead, with
        // the same error guard so an invalid field's label keeps reading red.
        [`&.${inputLabelClasses.focused}:not(.${inputLabelClasses.error})`]: {
          color: theme.palette.text.primary
        }
      })
    }
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.helperText
      })
    }
  },
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        ...theme.typography.inputText,
        // Native date/datetime picker glyphs are a black WebKit bitmap. Dark
        // mode inverts them so they match Text/Primary instead of disappearing
        // into the paper surface (e.g. announcement start/end date fields).
        ...(isDarkMode(theme)
          ? {
              "&::-webkit-calendar-picker-indicator": {
                filter: "invert(1)",
                cursor: "pointer"
              }
            }
          : {})
      })
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        ...theme.typography.tooltip,
        backgroundColor: theme.palette.misc.snackbarBackground,
        color: onSnackbarSurfaceColor(theme),
        padding: theme.spacing(1, 1.5),
        maxWidth: 320
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.misc.snackbarBackground
      })
    }
  },
  MuiLink: {
    styleOverrides: {
      // Followed links read Other/Visited Link in light mode and Other/Misc
      // Visited Link in dark mode. Dark mode also swaps the unvisited ink to
      // Other/Misc Dark Mode Link. Anchors styled as buttons are excluded
      // from those ink rules: `Button component={Link}` puts both classes on
      // the same `<a>`, and those read as controls wearing the button palette
      // rather than as inline link text. Underline geometry still applies to
      // every link.
      //
      // `textDecorationColor` has to be restated because MUI resolves the
      // underline from `--Link-underlineColor`, which it derives from the
      // `color` prop rather than from the ink we set here.
      root: ({ theme }) => {
        const visited = visitedLinkColor(theme);
        // Thickness and offset only render where a decoration line already
        // exists, so `underline="none"` links stay unlined on hover.
        const underlineStyles = {
          textUnderlineOffset: "3px",
          "&:hover": {
            textDecorationThickness: "4px",
            textUnderlineOffset: "2px"
          }
        };
        const visitedStyles = {
          "&:visited": {
            color: visited,
            textDecorationColor: alpha(visited, 0.4)
          }
        };

        if (!isDarkMode(theme)) {
          return {
            ...theme.typography.link,
            ...underlineStyles,
            [`&:not(.${buttonBaseClasses.root})`]: visitedStyles
          };
        }

        const link = darkModeLinkColor(theme);

        return {
          ...theme.typography.link,
          ...underlineStyles,
          [`&:not(.${buttonBaseClasses.root})`]: {
            color: link,
            textDecorationColor: alpha(link, 0.4),
            ...visitedStyles
          }
        };
      }
    }
  },
  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.avatarLetter
      })
    }
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        "> .MuiFormLabel-asterisk": {
          color: primaryAccentColor(theme),
          fontWeight: theme.typography.fontWeightBold
        }
      })
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        cursor: "pointer"
      }
    }
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        minHeight: "0 !important",
        "&.Mui-expanded": {
          marginTop: 0,
          minHeight: "0 !important"
        },
        ".MuiAccordionSummary-root": {
          minHeight: "0 !important",
          ".MuiAccordionSummary-content": {
            minHeight: "0 !important",
            ".MuiTypography-root": {
              cursor: "pointer"
            },
            "&.Mui-expanded": {
              minHeight: "0 !important",
              marginTop: "12px",
              marginBottom: "12px"
            }
          }
        }
      }
    }
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        ".Mui-disabled": {
          cursor: "not-allowed"
        }
      }
    }
  },
  MuiTabs: {
    styleOverrides: {
      indicator: ({ theme }) => ({
        backgroundColor: primaryAccentColor(theme)
      })
    }
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-selected": {
          color: selectedTabLabelColor(theme)
        }
      })
    }
  },
  MuiAlert: {
    variants: alertSeverities.map((severity) => ({
      props: { variant: "standard", severity },
      style: ({ theme }: { theme: Theme }) =>
        alertSeverityStyles(theme, severity)
    }))
  }
  // MuiCssBaseline above no longer carries the Montserrat @font-face rules it
  // once did. Those are now in src/styles/App.scss (loaded globally via
  // App.tsx's `import "./styles/App.scss"`), which keeps the exact same fonts
  // at the exact same URLs without routing them through the MUI theme.
};

type StyleCallback = (props: { theme: Theme }) => unknown;

/**
 * Runs a theme style callback once per color scheme and lets CSS pick.
 *
 * The callbacks above branch on {@link isDarkMode} and read `theme.palette`
 * directly, which on its own would bake whichever scheme rendered first into
 * the class. Prerendered pages render in light mode, so a dark mode reader saw
 * light controls until React hydrated. See `schemeStyles` for how the two
 * results become one class.
 */
const withColorSchemes =
  (callback: StyleCallback): StyleCallback =>
  (props) =>
    schemeStyles(props.theme, (schemeTheme) =>
      Object(callback({ ...props, theme: schemeTheme }))
    );

const wrapStyle = <T>(style: T): T =>
  (typeof style === "function"
    ? withColorSchemes(style as StyleCallback)
    : style) as T;

const withColorSchemeStyles = (
  overrides: Components<Theme>
): Components<Theme> =>
  Object.fromEntries(
    Object.entries(overrides).map(([name, override]) => {
      if (name === "MuiCssBaseline" || !override) return [name, override];

      const { styleOverrides, variants } = override as {
        styleOverrides?: unknown;
        variants?: { style?: unknown }[];
      };
      const wrapped: Record<string, unknown> = { ...override };

      if (typeof styleOverrides === "function") {
        wrapped.styleOverrides = wrapStyle(styleOverrides);
      } else if (styleOverrides && typeof styleOverrides === "object") {
        wrapped.styleOverrides = Object.fromEntries(
          Object.entries(styleOverrides).map(([slot, style]) => [
            slot,
            wrapStyle(style)
          ])
        );
      }

      if (variants) {
        wrapped.variants = variants.map((variant) => ({
          ...variant,
          style: wrapStyle(variant.style)
        }));
      }

      return [name, wrapped];
    })
  );

export const components: Components<Theme> =
  withColorSchemeStyles(componentOverrides);
